import React, { useState, useCallback, useMemo } from 'react'
import { DataGrid as MUIDataGrid, GridColumnHeaders, GridRow, ptBR } from '@mui/x-data-grid'
import PropTypes from 'prop-types'
import { styled } from '@mui/material'
import { CustomGridPagination } from './Customized'

const MemoizedRow = React.memo(GridRow)
const MemoizedColumnHeaders = React.memo(GridColumnHeaders)

const CustomDataGrid = styled(MUIDataGrid)({
  border: 'none',
  '.custom-data-grid-component-list-virtual-scroller': {
    minHeight: 'fit-content',
  },
  '&.custom-data-grid-component-list-root .custom-data-grid-component-list-cell:focus-within': {
    outline: 'none !important',
  },
  '.custom-data-grid-component-list-columnHeader:focus-within': {
    outline: 'none',
  },
  '.custom-data-grid-component-list-columnHeader': {
    cursor: 'pointer',
  },
  '.custom-data-grid-component-list-columnHeaders': {
    width: '100%',
  },
  '.custom-data-grid-component-list-columnHeaderTitleContainerContent': {
    width: '100%',
    fontWeight: 'bold',
    whiteSpace: 'normal', // Fixed typo: was 'wrapSpace'
  },
  '.HiTheme-DataGrid-overlayWrapperInner, .HiTheme-DataGrid-overlayWrapper': {
    height: '473px',
  },

  // Handle hover states for pinned column
  '&.custom-data-grid-component-list-root .MuiDataGrid-row:hover .custom-data-grid-component-list-cell:last-child': {
    backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
  },

  // Ensure proper scrolling behavior
  '&.custom-data-grid-component-list-root .custom-data-grid-component-list-virtual-scroller': {
    overflow: 'auto',
    transform: 'none !important',
  },
})

/**
 * DataGridComponent is a customizable wrapper around MUI's DataGrid, providing enhanced styling,
 * localization, and features such as server/client pagination, sorting, and optional pinning of the last column.
 *
 * @component
 *
 * @param {Object} props - The properties object.
 * @param {Object[]} props.rows - The data rows to be displayed in the grid.
 * @param {Object[]} props.columns - The column definitions for the grid.
 * @param {function} props.getRowId - Function to return a unique ID for each row.
 * @param {boolean} [props.loading=false] - If true, the grid will display a loading overlay.
 * @param {Object} [props.pagination] - Pagination configuration.
 * @param {number} [props.pagination.page=0] - The current page number (zero-based).
 * @param {number} [props.pagination.pageSize=10] - The number of rows per page.
 * @param {'client'|'server'} [props.pagination.paginationMode='server'] - Determines if pagination is handled on the client or server.
 * @param {number[]} [props.pagination.pageSizeOptions=[10, 25, 50, 100]] - The available page size options.
 * @param {function} [props.pagination.onPaginationModelChange=null] - Callback fired when the pagination model changes.
 * @param {Object} [props.sorting] - Sorting configuration.
 * @param {Object[]} [props.sorting.sortModel=[]] - The current sort model.
 * @param {function} [props.sorting.onSortModelChange=null] - Callback fired when the sort model changes.
 * @param {number} [props.rowCount=0] - The total number of rows (for server-side pagination).
 * @param {boolean} [props.pinLastColumn=false] - If true, pins the last column to the right.
 * @param {Object} [props.slots={}] - Custom slot components for the grid.
 * @param {...Object} [otherProps] - Additional props passed to the underlying DataGrid.
 *
 * @returns {JSX.Element} The rendered DataGrid component.
 */
export function DataGridComponent({
  rows = [],
  columns = [],
  getRowId = null,
  loading = false,
  pagination,
  rowCount = 0,
  pinLastColumn = false,
  sorting = {},
  slots = {},
  ...otherProps
}) {
  const [sortModel, setSortModel] = useState(sorting?.sortModel || [])

  const handleSortChange = useCallback(
    (newSortModel) => {
      setSortModel(newSortModel)
      if (sorting?.onSortModelChange) {
        sorting?.onSortModelChange(newSortModel)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sorting?.onSortModelChange],
  )

  const handlePaginationModelChange = useCallback(
    (model) => {
      if (pagination?.onPaginationModelChange) {
        const { page: current, pageSize: size } = model
        pagination?.onPaginationModelChange({ current, size })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pagination?.onPaginationModelChange],
  )

  const GridComponent = useMemo(() => {
    if (!pinLastColumn) return CustomDataGrid

    return styled(CustomDataGrid)({
      '&[data-pin-last-column="true"]': {
        overflow: 'hidden',
        position: 'relative',
        zIndex: 1,
        '.custom-data-grid-component-list-cell:last-child': {
          position: 'sticky !important',
          right: '0 !important',
          zIndex: '500 !important',
          backgroundColor: '#fff !important',
          borderLeft: '1px solid rgba(224, 224, 224, 1) !important',
          boxShadow: '-4px 0 4px rgba(0, 0, 0, 0.1) !important',
        },
      },
    })
  }, [pinLastColumn])

  const initialStateDataGrid = useMemo(() => {
    return {
      editable: false,
      columns,
      rows,
    }
  }, [rows, columns])

  // Memoize pagination model to prevent object recreation
  const paginationModel = useMemo(
    () => ({
      page: pagination?.page || 0,
      pageSize: pagination?.pageSize || 10,
    }),
    [pagination?.page, pagination?.pageSize],
  )

  // Memoize slots to prevent recreation
  const memoizedSlots = useMemo(
    () => ({
      pagination: CustomGridPagination,
      row: MemoizedRow,
      columnHeaders: MemoizedColumnHeaders,
      ...slots,
    }),
    [slots],
  )

  return (
    <GridComponent
      {...initialStateDataGrid}
      data-testid="custom-data-grid-component"
      data-pin-last-column={pinLastColumn}
      paginationModel={paginationModel}
      classes={{
        root: 'custom-data-grid-component-list-root',
        cell: 'custom-data-grid-component-list-cell',
        columnHeader: 'custom-data-grid-component-list-columnHeader',
        columnHeaderTitleContainer: 'custom-data-grid-component-list-columnHeaderTitleContainerContent',
        columnHeaders: 'custom-data-grid-component-list-columnHeaders',
        virtualScroller: 'custom-data-grid-component-list-virtual-scroller',
      }}
      slots={memoizedSlots}
      getRowId={getRowId}
      sortingOrder={['asc', 'desc']}
      sortModel={sortModel}
      onSortModelChange={handleSortChange}
      paginationMode={pagination.paginationMode}
      pageSizeOptions={pagination.pageSizeOptions}
      onPaginationModelChange={handlePaginationModelChange}
      rowCount={rowCount}
      loading={loading}
      localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
      {...otherProps}
    />
  )
}

DataGridComponent.propTypes = {
  rows: PropTypes.instanceOf(Array).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.oneOfType([PropTypes.string, PropTypes.symbol]).isRequired,
      headerName: PropTypes.string,
      width: PropTypes.number,
      minWidth: PropTypes.number,
      maxWidth: PropTypes.number,
      flex: PropTypes.number,
      sortable: PropTypes.bool,
      filterable: PropTypes.bool,
      hideable: PropTypes.bool,
      resizable: PropTypes.bool,
      type: PropTypes.oneOf(['string', 'number', 'date', 'boolean', 'actions']),
      align: PropTypes.oneOf(['left', 'center', 'right']),
      headerAlign: PropTypes.oneOf(['left', 'center', 'right']),
      renderCell: PropTypes.func,
      renderHeader: PropTypes.func,
      valueGetter: PropTypes.func,
      valueFormatter: PropTypes.func,
      hide: PropTypes.bool,
    }),
  ).isRequired,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    pageSize: PropTypes.number,
    paginationMode: PropTypes.oneOf(['client', 'server']),
    pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
    onPaginationModelChange: PropTypes.func,
  }),
  sorting: PropTypes.shape({
    sortModel: PropTypes.instanceOf(Array),
    onSortModelChange: PropTypes.func,
  }),
  loading: PropTypes.bool,
  rowCount: PropTypes.number,
  getRowId: PropTypes.func.isRequired,
  pinLastColumn: PropTypes.bool,
  slots: PropTypes.instanceOf(Object),
}

DataGridComponent.defaultProps = {
  loading: false,
  rowCount: 0,
  pinLastColumn: false,
  pagination: {
    page: 0,
    pageSize: 10,
    paginationMode: 'server',
    pageSizeOptions: [10, 25, 50, 100],
    onPaginationModelChange: null,
  },
  sorting: {
    sortModel: [],
    onSortModelChange: null,
  },
  slots: {},
}

export default DataGridComponent
